import { Fragment } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';

const getThemeColor = (lightColor, darkColor) =>
    global.gw.theme === 'light' ? lightColor : darkColor;

const parseMetricCell = (cellLine) => {
    const parts = (cellLine ?? '').split('|').map((p) => p.trim());
    const pctRaw = parts[0];
    let pct = null;
    if (pctRaw && pctRaw !== '—') {
        const n = parseInt(String(pctRaw).replace('%', ''), 10);
        if (Number.isFinite(n)) pct = Math.min(100, Math.max(0, n));
    }
    const bu = parts[1] && parts[1] !== '—' ? parts[1] : null;
    const lb = parts[2] && parts[2] !== '—' ? parts[2] : null;
    return { pct, bu, lb };
};

const InventoryProgressBar = ({ pct, variant = 'product' }) => {
    if (pct == null) {
        return (
            <MuiMaterials.Box
                sx={{
                    width: '100%',
                    maxWidth: { xs: 88, sm: 108 },
                    height: 6,
                    borderRadius: 999,
                    bgcolor: getThemeColor('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.08)'),
                }}
            />
        );
    }
    const fillColor =
        variant === 'product'
            ? getThemeColor('#5a7248', '#d4ad6a')
            : getThemeColor('#7a8794', '#8a9099');
    return (
        <MuiMaterials.Box
            sx={{
                width: '100%',
                maxWidth: { xs: 88, sm: 108 },
                height: 6,
                borderRadius: 999,
                bgcolor: getThemeColor('rgba(0, 0, 0, 0.07)', 'rgba(255, 255, 255, 0.1)'),
                overflow: 'hidden',
            }}
        >
            <MuiMaterials.Box
                sx={{
                    width: `${pct}%`,
                    height: '100%',
                    borderRadius: 999,
                    bgcolor: fillColor,
                    transition: 'width 0.35s ease',
                }}
            />
        </MuiMaterials.Box>
    );
};

const InventoryMetricCell = ({ cellLine, variant = 'product', compact = false }) => {
    const { pct, bu, lb } = parseMetricCell(cellLine);
    const text = getThemeColor('#333333', '#f4cc8e');
    const subtle = getThemeColor('#666666', '#a8abb1');

    return (
        <MuiMaterials.Box sx={{ minWidth: compact ? 120 : 140, py: 0.15 }}>
            <MuiMaterials.Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: bu || lb ? 0.45 : 0,
                }}
            >
                <MuiMaterials.Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: compact ? { xs: 13, sm: 14 } : { xs: 14, sm: 15 },
                        fontVariantNumeric: 'tabular-nums',
                        color: text,
                        minWidth: 36,
                    }}
                >
                    {pct != null ? `${pct}%` : '—'}
                </MuiMaterials.Typography>
                <InventoryProgressBar pct={pct} variant={variant} />
            </MuiMaterials.Box>
            {(bu || lb) ? (
                <MuiMaterials.Typography
                    sx={{
                        fontSize: compact ? { xs: 11, sm: 12 } : { xs: 12, sm: 13 },
                        fontVariantNumeric: 'tabular-nums',
                        color: subtle,
                        lineHeight: 1.3,
                    }}
                >
                    {[bu, lb].filter(Boolean).join(' · ')}
                </MuiMaterials.Typography>
            ) : null}
        </MuiMaterials.Box>
    );
};

const StatCard = ({ label, value, subValue, icon: Icon }) => {
    const text = getThemeColor('#333333', '#f4cc8e');
    const subtle = getThemeColor('#666666', '#a8abb1');
    const cardBg = getThemeColor('#ffffff', '#383838');
    const border = getThemeColor('rgba(0, 0, 0, 0.1)', 'rgba(244, 204, 142, 0.22)');

    return (
        <MuiMaterials.Box
            sx={{
                flex: '1 1 140px',
                minWidth: 0,
                p: { xs: 1.5, sm: 2 },
                borderRadius: '10px',
                border: `1px solid ${border}`,
                background: cardBg,
                boxShadow: getThemeColor(
                    '0 1px 3px rgba(0, 0, 0, 0.06)',
                    '0 2px 8px rgba(0, 0, 0, 0.25)',
                ),
            }}
        >
            <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                {Icon ? (
                    <MuiMaterials.Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            flexShrink: 0,
                            bgcolor: getThemeColor('rgba(0, 0, 0, 0.05)', 'rgba(244, 204, 142, 0.12)'),
                            color: getThemeColor('#555555', '#f4cc8e'),
                        }}
                    >
                        <Icon sx={{ fontSize: 20 }} />
                    </MuiMaterials.Box>
                ) : null}
                <MuiMaterials.Box sx={{ minWidth: 0, flex: 1 }}>
                    <MuiMaterials.Typography
                        sx={{
                            fontSize: { xs: 10, sm: 11 },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.55px',
                            color: subtle,
                            mb: 0.35,
                        }}
                    >
                        {label}
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: 18, sm: 22 },
                            fontVariantNumeric: 'tabular-nums',
                            color: text,
                            lineHeight: 1.15,
                        }}
                    >
                        {value}
                    </MuiMaterials.Typography>
                    {subValue ? (
                        <MuiMaterials.Typography
                            sx={{
                                mt: 0.35,
                                fontSize: { xs: 11, sm: 12 },
                                color: subtle,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {subValue}
                        </MuiMaterials.Typography>
                    ) : null}
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        </MuiMaterials.Box>
    );
};

const openBinInventoryEditor = (siteGuid, binId) => {
    if (!siteGuid || binId == null) return;
    window.dispatchEvent(
        new CustomEvent('grainwave-open-site-inventory-bin', {
            detail: { siteGuid, binId },
        }),
    );
};

const InventorySummaryTable = ({
    summary,
    siteGuid,
    onEditBin = null,
    showBinTable = true,
    expandedBinId = null,
    renderBinEditor = null,
}) => {
    if (!summary) return null;

    const handleEditBin = (binId) => {
        if (binId == null) return;
        if (typeof onEditBin === 'function') {
            onEditBin(binId);
            return;
        }
        openBinInventoryEditor(siteGuid, binId);
    };

    const byBin = showBinTable && Array.isArray(summary.byBin) ? summary.byBin : [];
    const supportsInlineEdit = typeof renderBinEditor === 'function';
    const colCount = 6;

    const text = getThemeColor('#333333', '#f4cc8e');
    const subtle = getThemeColor('#666666', '#a8abb1');
    const headerBg = getThemeColor('#ececec', '#252525');
    const headerBorder = getThemeColor('#d8d8d8', '#3d3d3d');
    const rowBorder = getThemeColor('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.06)');
    const rowHover = getThemeColor('rgba(0, 0, 0, 0.025)', 'rgba(244, 204, 142, 0.05)');
    const rowStripe = getThemeColor('rgba(0, 0, 0, 0.02)', 'rgba(255, 255, 255, 0.02)');
    const totalRowBg = getThemeColor('rgba(90, 114, 72, 0.08)', 'rgba(212, 173, 106, 0.1)');
    const cardBg = getThemeColor('#f8f8f8', '#313131');
    const cardBorder = getThemeColor('rgba(0, 0, 0, 0.12)', 'rgba(244, 204, 142, 0.28)');
    const tableSurface = getThemeColor('#ffffff', '#2a2a2a');

    const tableContainerSx = {
        flex: '0 0 auto',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        overflowY: 'visible',
        borderRadius: '8px',
        border: `1px solid ${headerBorder}`,
        background: tableSurface,
        boxShadow: getThemeColor(
            'inset 0 1px 0 rgba(255,255,255,0.8)',
            'inset 0 1px 0 rgba(255,255,255,0.04)',
        ),
        overscrollBehaviorY: 'auto',
        WebkitOverflowScrolling: 'touch',
        '& .MuiTableCell-root': {
            py: { xs: 1.1, sm: 1.25 },
            px: { xs: 1.15, sm: 1.5 },
            fontSize: { xs: '14px', sm: '15px' },
            lineHeight: 1.35,
            borderBottom: `1px solid ${rowBorder}`,
        },
        '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: { xs: '10px', sm: '11px' },
            textTransform: 'uppercase',
            letterSpacing: '0.55px',
            color: subtle,
            background: headerBg,
            borderBottom: `1px solid ${headerBorder}`,
            whiteSpace: 'nowrap',
        },
        '& tbody .MuiTableRow-root:last-of-type .MuiTableCell-root': {
            borderBottom: 'none',
        },
        '& tbody .MuiTableRow-root:not(.inventory-total-row):not(.inventory-bin-editor-row):hover .MuiTableCell-root': {
            background: rowHover,
        },
        '& tbody .MuiTableRow-root:nth-of-type(even):not(.inventory-total-row):not(.inventory-bin-editor-row) .MuiTableCell-root': {
            background: rowStripe,
        },
        '& tbody .MuiTableRow-root:nth-of-type(even):not(.inventory-total-row):not(.inventory-bin-editor-row):hover .MuiTableCell-root': {
            background: rowHover,
        },
        '& .inventory-bin-editor-row .MuiTableCell-root': {
            background: `${getThemeColor('#e8e8e8', '#1e1e1e')} !important`,
        },
        '& .inventory-bin-editor-row:hover .MuiTableCell-root': {
            background: `${getThemeColor('#e8e8e8', '#1e1e1e')} !important`,
        },
        '& .inventory-total-row .MuiTableCell-root': {
            background: totalRowBg,
            fontWeight: 600,
            color: text,
            borderBottom: `1px solid ${getThemeColor('rgba(90, 114, 72, 0.18)', 'rgba(212, 173, 106, 0.22)')}`,
        },
        '& tbody .MuiTableCell-root': {
            color: text,
            fontWeight: 500,
        },
    };

    const binTableContainerSx = {
        ...tableContainerSx,
        touchAction: 'pan-x pan-y',
        overscrollBehaviorX: 'contain',
    };

    const sectionCardSx = {
        width: '100%',
        boxSizing: 'border-box',
        p: { xs: '14px 16px', sm: '18px 22px' },
        borderRadius: '12px',
        border: `1px solid ${cardBorder}`,
        background: cardBg,
        boxShadow: getThemeColor(
            '0 2px 8px rgba(0, 0, 0, 0.04)',
            '0 4px 16px rgba(0, 0, 0, 0.2)',
        ),
    };

    const productSub =
        [summary.bushelsLabel, summary.poundsLabel].filter(Boolean).join(' · ') || null;
    const spaceSub =
        [summary.headroomBushelsLabel, summary.headroomPoundsLabel].filter(Boolean).join(' · ') || null;

    return (
        <MuiMaterials.Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, sm: 2.5 },
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
            }}
        >
            <MuiMaterials.Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: { xs: 1.25, sm: 1.5 },
                }}
            >
                <StatCard
                    label="Bins"
                    value={summary.totalBinCount}
                    subValue={
                        summary.binsWithProduct > 0
                            ? `${summary.binsWithProduct} with product`
                            : null
                    }
                    icon={StorageOutlinedIcon}
                />
                <StatCard
                    label="Product"
                    value={summary.fillPctRounded != null ? `${summary.fillPctRounded}%` : '—'}
                    subValue={productSub}
                    icon={Inventory2OutlinedIcon}
                />
                <StatCard
                    label="Space"
                    value={summary.spacePctRounded != null ? `${summary.spacePctRounded}%` : '—'}
                    subValue={spaceSub}
                />
                <StatCard
                    label="Capacity"
                    value={summary.siteTotalCapacityLabel ?? '—'}
                    subValue="Total storability"
                />
            </MuiMaterials.Box>

            <MuiMaterials.Box sx={sectionCardSx}>
                <MuiMaterials.Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                    <MuiMaterials.Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '16px', sm: '18px' },
                            color: text,
                            letterSpacing: '0.01em',
                        }}
                    >
                        Site Inventory
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography
                        sx={{
                            mt: 0.35,
                            fontSize: { xs: 12, sm: 13 },
                            color: subtle,
                        }}
                    >
                        Totals by grain type across all bins
                    </MuiMaterials.Typography>
                </MuiMaterials.Box>
                <MuiMaterials.TableContainer
                    component={MuiMaterials.Paper}
                    elevation={0}
                    sx={tableContainerSx}
                    title="Bin-level snapshot by grain. Product / space cells: % | bu | lb (K/M = thousands/millions, one decimal)."
                >
                    <MuiMaterials.Table sx={{ borderCollapse: 'collapse', width: '100%' }}>
                        <MuiMaterials.TableHead>
                            <MuiMaterials.TableRow>
                                <MuiMaterials.TableCell>Grain</MuiMaterials.TableCell>
                                <MuiMaterials.TableCell align="center">Bins</MuiMaterials.TableCell>
                                <MuiMaterials.TableCell>
                                    <MuiMaterials.Tooltip
                                        title="Percent full, bushels, pounds (volumes first, then mass)."
                                        arrow
                                    >
                                        <span>Product</span>
                                    </MuiMaterials.Tooltip>
                                </MuiMaterials.TableCell>
                                <MuiMaterials.TableCell>
                                    <MuiMaterials.Tooltip
                                        title="Percent space (unfilled bin volume), bushels, pounds."
                                        arrow
                                    >
                                        <span>Space</span>
                                    </MuiMaterials.Tooltip>
                                </MuiMaterials.TableCell>
                                <MuiMaterials.TableCell>
                                    <MuiMaterials.Tooltip
                                        title="Sum of each bin's max bushel-equivalent storability."
                                        arrow
                                    >
                                        <span>Capacity</span>
                                    </MuiMaterials.Tooltip>
                                </MuiMaterials.TableCell>
                            </MuiMaterials.TableRow>
                        </MuiMaterials.TableHead>
                        <MuiMaterials.TableBody>
                            <MuiMaterials.TableRow className="inventory-total-row">
                                <MuiMaterials.TableCell>Site total</MuiMaterials.TableCell>
                                <MuiMaterials.TableCell align="center">
                                    {summary.totalBinCount}
                                </MuiMaterials.TableCell>
                                <MuiMaterials.TableCell>
                                    <InventoryMetricCell cellLine={summary.productCellLine} variant="product" />
                                </MuiMaterials.TableCell>
                                <MuiMaterials.TableCell>
                                    <InventoryMetricCell cellLine={summary.headspaceCellLine} variant="space" />
                                </MuiMaterials.TableCell>
                                <MuiMaterials.TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {summary.siteTotalCapacityLabel ?? '—'}
                                </MuiMaterials.TableCell>
                            </MuiMaterials.TableRow>
                            {summary.byGrain.map((g) => (
                                <MuiMaterials.TableRow key={g.grainKey}>
                                    <MuiMaterials.TableCell>{g.label}</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell align="center">{g.binCount}</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>
                                        <InventoryMetricCell cellLine={g.productCellLine} variant="product" compact />
                                    </MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>
                                        <InventoryMetricCell cellLine={g.headspaceCellLine} variant="space" compact />
                                    </MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {g.capacityCellLine ?? '—'}
                                    </MuiMaterials.TableCell>
                                </MuiMaterials.TableRow>
                            ))}
                        </MuiMaterials.TableBody>
                    </MuiMaterials.Table>
                </MuiMaterials.TableContainer>
            </MuiMaterials.Box>

            {byBin.length > 0 ? (
                <MuiMaterials.Box sx={sectionCardSx}>
                    <MuiMaterials.Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                        <MuiMaterials.Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '16px', sm: '18px' },
                                color: text,
                            }}
                        >
                            Bin Inventory
                        </MuiMaterials.Typography>
                        <MuiMaterials.Typography
                            sx={{
                                mt: 0.35,
                                fontSize: { xs: 12, sm: 13 },
                                color: subtle,
                            }}
                        >
                            Per-bin breakdown · Click a row to edit under that bin
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                    <MuiMaterials.TableContainer
                        component={MuiMaterials.Paper}
                        elevation={0}
                        sx={binTableContainerSx}
                    >
                        <MuiMaterials.Table
                            sx={{
                                borderCollapse: 'collapse',
                                width: '100%',
                                minWidth: { xs: 680, sm: '100%' },
                            }}
                        >
                            <MuiMaterials.TableHead>
                                <MuiMaterials.TableRow>
                                    <MuiMaterials.TableCell>Bin</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>Grain</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>Product</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>Space</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell>Capacity</MuiMaterials.TableCell>
                                    <MuiMaterials.TableCell align="center" sx={{ width: 52, minWidth: 52, px: 0.5 }}>
                                        <span aria-hidden="true">&nbsp;</span>
                                    </MuiMaterials.TableCell>
                                </MuiMaterials.TableRow>
                            </MuiMaterials.TableHead>
                            <MuiMaterials.TableBody>
                                {byBin.map((bin) => {
                                    const isExpanded = supportsInlineEdit
                                        && expandedBinId != null
                                        && bin.binId === expandedBinId;
                                    const canEdit = bin.binId != null
                                        && (typeof onEditBin === 'function' || !!siteGuid);
                                    return (
                                        <Fragment key={`${bin.binId ?? 'x'}-${bin.binName}`}>
                                            <MuiMaterials.TableRow
                                                data-inventory-bin-row={bin.binId}
                                                onClick={canEdit
                                                    ? () => handleEditBin(bin.binId)
                                                    : undefined}
                                                sx={{
                                                    cursor: canEdit ? 'pointer' : 'default',
                                                    ...(canEdit ? {
                                                        '&:hover .MuiTableCell-root': {
                                                            backgroundColor: getThemeColor(
                                                                'rgba(0, 0, 0, 0.03)',
                                                                'rgba(244, 204, 142, 0.06)',
                                                            ),
                                                        },
                                                    } : null),
                                                    ...(isExpanded ? {
                                                        '& .MuiTableCell-root': { borderBottom: 'none' },
                                                    } : null),
                                                }}
                                            >
                                                <MuiMaterials.TableCell sx={{ fontWeight: 600 }}>
                                                    {bin.binName}
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ color: subtle }}>
                                                    {bin.grainLabel}
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell>
                                                    <InventoryMetricCell cellLine={bin.productCellLine} variant="product" compact />
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell>
                                                    <InventoryMetricCell cellLine={bin.headspaceCellLine} variant="space" compact />
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {bin.capacityCellLine ?? '—'}
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell align="center" sx={{ width: 52, minWidth: 52, px: 0.5 }}>
                                                    <MuiMaterials.Tooltip
                                                        title={isExpanded
                                                            ? 'Collapse editor'
                                                            : 'View or edit inventory for this bin.'}
                                                        arrow
                                                    >
                                                        <span>
                                                            <MuiMaterials.IconButton
                                                                size="small"
                                                                aria-label={isExpanded
                                                                    ? `Collapse editor for ${bin.binName}`
                                                                    : `Edit inventory for ${bin.binName}`}
                                                                aria-expanded={isExpanded}
                                                                disabled={!canEdit}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditBin(bin.binId);
                                                                }}
                                                                sx={{
                                                                    color: text,
                                                                    border: `1px solid ${isExpanded
                                                                        ? getThemeColor('rgba(0,0,0,0.28)', 'rgba(244,204,142,0.55)')
                                                                        : getThemeColor('rgba(0,0,0,0.12)', 'rgba(244,204,142,0.25)')}`,
                                                                    borderRadius: '8px',
                                                                    width: 34,
                                                                    height: 34,
                                                                    backgroundColor: isExpanded
                                                                        ? getThemeColor('rgba(0, 0, 0, 0.06)', 'rgba(244, 204, 142, 0.14)')
                                                                        : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: getThemeColor(
                                                                            'rgba(0, 0, 0, 0.06)',
                                                                            'rgba(244, 204, 142, 0.14)',
                                                                        ),
                                                                        borderColor: getThemeColor(
                                                                            'rgba(0,0,0,0.2)',
                                                                            'rgba(244,204,142,0.45)',
                                                                        ),
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        color: getThemeColor(
                                                                            'rgba(0, 0, 0, 0.26)',
                                                                            'rgba(244, 204, 142, 0.35)',
                                                                        ),
                                                                        borderColor: 'transparent',
                                                                    },
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: { xs: 17, sm: 18 } }} />
                                                            </MuiMaterials.IconButton>
                                                        </span>
                                                    </MuiMaterials.Tooltip>
                                                </MuiMaterials.TableCell>
                                            </MuiMaterials.TableRow>
                                            {supportsInlineEdit && isExpanded ? (
                                                <MuiMaterials.TableRow className="inventory-bin-editor-row">
                                                    <MuiMaterials.TableCell
                                                        colSpan={colCount}
                                                        sx={{
                                                            py: 0,
                                                            px: 0,
                                                            borderBottom: `1px solid ${rowBorder}`,
                                                            background: `${getThemeColor('#e8e8e8', '#1e1e1e')} !important`,
                                                        }}
                                                    >
                                                        <MuiMaterials.Box
                                                            data-inventory-bin-id={bin.binId}
                                                            sx={{
                                                                px: { xs: 1.5, sm: 2 },
                                                                py: { xs: 1.5, sm: 2 },
                                                                background: getThemeColor('#e8e8e8', '#1e1e1e'),
                                                            }}
                                                        >
                                                            {renderBinEditor(bin)}
                                                        </MuiMaterials.Box>
                                                    </MuiMaterials.TableCell>
                                                </MuiMaterials.TableRow>
                                            ) : null}
                                        </Fragment>
                                    );
                                })}
                            </MuiMaterials.TableBody>
                        </MuiMaterials.Table>
                    </MuiMaterials.TableContainer>
                </MuiMaterials.Box>
            ) : null}
        </MuiMaterials.Box>
    );
};

export default InventorySummaryTable;
